import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("simpa_token", data.token);
      localStorage.setItem("simpa_user", JSON.stringify(data.user));
      toast.success("Berhasil masuk. Selamat datang!");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-gradient-to-br from-blue-700 via-blue-600 to-blue-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-32 top-1/3 w-96 h-96 rounded-full bg-white/10" />
        <div className="absolute -left-24 -bottom-24 w-80 h-80 rounded-full bg-gold-500/10" />
        <Link to="/" className="inline-flex relative group" data-testid="login-logo-link">
          <img src="/logo-mark.png" alt="SIMPA" className="h-28 w-auto drop-shadow-[0_12px_24px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:scale-105" />
        </Link>
        <div className="relative">
          <h1 className="text-4xl font-semibold tracking-tight leading-tight">Kelola arsip formulir <br /> dengan lebih tenang.</h1>
          <p className="mt-4 text-blue-100 max-w-md leading-relaxed">Dashboard admin memberi Anda visibilitas penuh atas seluruh pengajuan pemohon secara real-time.</p>
        </div>
        <div className="relative text-sm text-blue-100">© 2026 Kantor Imigrasi Kelas II TPI Belu</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="back-to-home-link">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
          </Link>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Masuk Petugas / Admin</h2>
          <p className="mt-2 text-slate-500">Halaman ini khusus untuk petugas dan administrator SIMPA.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@kantor.go.id"
                className="mt-2 h-11 rounded-lg"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-11 rounded-lg"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg btn-lift bg-blue-600 hover:bg-blue-700"
              data-testid="login-submit-btn"
            >
              <LogIn className="w-4 h-4 mr-2" /> {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-400">
            Hanya petugas dan administrator yang berwenang. Hubungi admin kantor untuk kredensial.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
